import {
    Component,
    ElementRef,
    HostBinding,
    OnDestroy,
    OnInit,
    inject,
    input,
    viewChild,
    viewChildren,
} from '@angular/core';
import {
    ActivatedRoute,
    Router,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
} from '@angular/router';
import {
    TabsChildrenHorizontalAnimation,
    MultipleEventsDirective,
    AvatarComponent,
    SeparatorComponent,
} from '@exl-ng/mulo-common';
import { ProfileOutputComponent } from './profile-output/profile-output.component';
import { UrlUtils } from '../shared/utils/url.utils';
import { ProfileService } from './profile.service';
import { JwtUtilService } from '../core/jwt-util.service';
import { State } from '../shared/configurations/state.constant';
import { ResearcherProfile } from '../shared/interfaces/researcher-profile.interface';
import { SettingsService } from '../settings/settings.service';
import { ProfileDataService } from './profile-data.service';
import { ResearcherOverview } from '../shared/interfaces/researcher-overview.interface';
import { SEOService } from '../core/seo.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { WindowRefService } from '../core/window-ref.service';
import { SEO } from '../shared/configurations/seo.constant';
import { ResearcherName } from '../shared/interfaces/researcher-name.model';
import { Identifier } from '../shared/interfaces/identifier.model';
import { PersonOrganizationAffiliationDataWrapper } from '../shared/interfaces/person-organization-affiliation-data-wrapper.model';
import { ConfigurationHandlerService } from '../core/configuration-handler.service';
import { ResearcherAsset } from '../shared/interfaces/researcher-asset.interface';
import { MediaService, SvgViewboxDirective } from '@exl-ng/mulo-core';
import { AffiliationService } from '../settings/sections/settings-affiliations/affiliation.service';
import { Award } from '../shared/interfaces/award.interface';
import { ProfileProjectsService } from './profile-projects/profile-projects.service';
import {
    catchError,
    concatMap,
    forkJoin,
    from,
    map,
    of,
    pipe,
    ReplaySubject,
    Subscription,
    tap,
    toArray,
} from 'rxjs';
import { ProfileTabs } from '../shared/configurations/profile.constant';
import { ProfileActivitiesService } from './profile-activities/profile-activities.service';
import { ProfileMentionsService } from './profile-mentions/profile-mentions.service';
import { DOCUMENT, Location, NgClass } from '@angular/common';
import { RouterService } from '../core/router.service';
import { PortalActivityTabConfiguration } from '../shared/interfaces/view-configuration/portal-activity-tab-configuration.interface';
import { SelectedFacet } from '../shared/interfaces/list-data-params';
import { MatTabLink, MatTabNav, MatTabNavPanel } from '@angular/material/tabs';
import { TagComponent } from '../parts/tags/tag/tag.component';
import { TagsComponent } from '../parts/tags/tags.component';
import { ExpandableTextComponent } from '../parts/expandable-text/expandable-text.component';
import { PersonBoxComponent } from '../parts/person-box/person-box.component';
import { MatIcon } from '@angular/material/icon';
import { MatAnchor, MatButton } from '@angular/material/button';
import {
    CenteredBlockLayoutComponent,
    SideMainLayoutComponent,
    NavMainLayoutComponent,
} from '@exl-ng/mulo-views';
import { DefaultTranslatePipe } from '../shared/pipes/default-translate.pipe';

@Component({
    selector: 'esp-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.scss'],
    animations: [TabsChildrenHorizontalAnimation],
    imports: [
        CenteredBlockLayoutComponent,
        SideMainLayoutComponent,
        MatAnchor,
        MultipleEventsDirective,
        MatIcon,
        PersonBoxComponent,
        MatButton,
        SvgViewboxDirective,
        AvatarComponent,
        NgClass,
        RouterLink,
        ExpandableTextComponent,
        TagsComponent,
        TagComponent,
        SeparatorComponent,
        NavMainLayoutComponent,
        MatTabNav,
        MatTabLink,
        RouterLinkActive,
        RouterOutlet,
        MatTabNavPanel,
        TranslateModule,
        DefaultTranslatePipe,
    ],
})
export class ProfileComponent implements OnInit, OnDestroy {
    private route = inject(ActivatedRoute);
    profileService = inject(ProfileService);
    private jwtUtilService = inject(JwtUtilService);
    private router = inject(Router);
    private settingsService = inject(SettingsService);
    profileDataService = inject(ProfileDataService);
    private SEOService = inject(SEOService);
    private translate = inject(TranslateService);
    private windowRefService = inject(WindowRefService);
    private configSvc = inject(ConfigurationHandlerService);
    media = inject(MediaService);
    affiliationService = inject(AffiliationService);
    private profileProjectsService = inject(ProfileProjectsService);
    private profileActivitiesService = inject(ProfileActivitiesService);
    private profileMentionsService = inject(ProfileMentionsService);
    private document = inject<Document>(DOCUMENT);
    _window = this.document.defaultView;
    private location = inject(Location);
    private routerService = inject(RouterService);

    isDataLoaded = false;
    hasFixedHeader = false;
    institutionCode: string;
    profileImage: any;
    leftColumnFixed;
    leftColumnStyles;

    nameSubscription: Subscription;

    readonly person = input(undefined);

    readonly leftColumn = viewChild<ElementRef>('leftColumn');
    readonly header = viewChild('header', { read: ElementRef });
    readonly title = viewChild('title', { read: ElementRef });
    readonly tabs = viewChildren(MatTabLink);
    readonly child = viewChild(ProfileOutputComponent);
    @HostBinding('class.esp-profile') true;
    @HostBinding('class.exl-fixed-header') fixedHeaderClass: boolean =
        this.hasFixedHeader;
    signedIn;
    linkForCanonicalURL: string;
    mainInstName;
    mainInstCode;
    userID;

    s1Finish = false;
    s2Finish = false;
    s3Finish = false;
    s4Finish = false;

    selectedIndex = 0;
    tabDirection: string;
    tabTravelDistance = '50px';

    _onDestroy = new ReplaySubject<void>();

    prevUrl: any;
    enableCV = false;

    _descText: string;

    constructor() {
        const configSvc = this.configSvc;

        this.profileDataService.createForm();
        this.profileDataService.researcherHighlights = [];
        this.mainInstCode = this.configSvc.mainInstitutionCode();
        this.translate.get(this.mainInstCode || '_').subscribe((res) => {
            this.mainInstName = res;
        });
        this.enableCV =
            this.configSvc.isEsploroEditionAdvanced() &&
            (configSvc.researcherProfileGeneralConfiguration()
                .enableCreationOfCV ||
                this.isAdminLoggedIn);
    }

    ngOnInit() {
        this.signedIn = this.jwtUtilService.isLoggedIn();
        this.institutionCode = UrlUtils.getParam('institution');
        this.userID = this.profileService.getUserID();
        this.loadData(this.userID);
        this.addTabs();
        if (this.profileService.isPublicPage) {
            this.loadProfileImageForPublicDisplay(this.userID);
            this.loadResearcherHighlights();
            this.loadResearcherGrantsHighlights();
        } else {
            this.loadProfileImage(this.userID);
        }
        this.tabDirection = this.tabTravelDistance;
        const prevUrl = (this.location.getState() as any)?.prevUrl;
        this.prevUrl = prevUrl === '/' ? null : this.router.parseUrl(prevUrl);
    }

    get isAdminLoggedIn(): boolean {
        return this.jwtUtilService.isAdminLoggedIn();
    }

    updateSelectedIndex(index) {
        this.tabDirection = this.setTabDir(this.selectedIndex, index);
        this.selectedIndex = index;
    }

    handleActive(index: number, isActive: boolean) {
        const tabLink: HTMLLinkElement =
            this.tabs().at(index).elementRef.nativeElement;
        tabLink.tabIndex = isActive ? 0 : -1;
    }

    loadResearcherHighlights() {
        this.profileService
            .getResearcherHighlights(this.userID)
            .subscribe((data) => {
                this.profileDataService.researcherHighlights =
                    data as ResearcherAsset[];
            });
    }

    loadResearcherGrantsHighlights() {
        this.profileService
            .getResearcherGrantsHighlights(this.userID)
            .subscribe((data) => {
                this.profileDataService.researcherGrantsHighlights =
                    data as Award[];
            });
    }

    setTabDir(old, next) {
        let string;
        if (old > next) {
            string = '-' + this.tabTravelDistance;
        } else {
            string = this.tabTravelDistance;
        }
        return string;
    }

    addTabs() {
        this.profileService.sections = [ProfileTabs.OVERVIEW];
        const joinedTabs$ = !this.profileService.isPublicPage
            ? this.getTabsForProfile()
            : this.getTabsForPublicProfile();
        joinedTabs$.subscribe((results) => {
            const tabs = [].concat(...results)?.filter(Boolean);
            this.profileService.sections = [
                ...this.profileService.sections,
                ...tabs,
            ];
        });
    }

    getTabsForProfile() {
        return forkJoin(
            //return an observable of string to keep same numer of calls in forkjoin
            of('output'),
            this.profileService
                .displayAwardsTab()
                .pipe(this.tabFilters$(ProfileTabs.AWARDS)),
            this.profileMentionsService
                .displayMediaMentionsTab()
                .pipe(this.tabFilters$(ProfileTabs.MENTIONS)),
            this.displayProjectTab(),
            this.displayActivitiesTab(),
        );
    }

    getTabsForPublicProfile() {
        return forkJoin(
            this.profileService
                .displayOutputTabInPublicProfile(this.userID)
                .pipe(this.tabFilters$(ProfileTabs.OUTPUT)),
            this.profileService
                .displayAwardsTabInPublicProfile(this.userID)
                .pipe(this.tabFilters$(ProfileTabs.AWARDS)),
            this.profileMentionsService
                .displayMediaMentionsTabInPublicProfile(this.userID)
                .pipe(this.tabFilters$(ProfileTabs.MENTIONS)),
            this.displayProjectTab(),
            this.displayActivitiesTab(),
        );
    }

    tabFilters$ = (tab: string) =>
        pipe(
            map((data) => (data ? tab : null)),
            catchError((error) => of(error)),
        );

    displayProjectTab() {
        if (
            this.configSvc.isEsploroEditionAdvanced() ||
            this.configSvc.getCustomerParameter('esploro_projects') === 'true'
        ) {
            return this.profileProjectsService
                .displayProjectsTabs()
                .pipe(this.tabFilters$(ProfileTabs.PROJECTS));
        }
        return of(null);
    }

    displayActivitiesTab() {
        if (
            this.configSvc.isEsploroEditionAdvanced() ||
            this.configSvc.isCustomerParameterEnabled('esploro_activities')
        ) {
            const relevantTabsByCategories$ = (
                tab: PortalActivityTabConfiguration,
            ) => {
                const facetObject = (category: string): SelectedFacet => {
                    return { key: category, isInclude: true };
                };
                const categoryFacetsObject = {
                    ['category_facet']: tab.categories?.map(facetObject),
                };

                return this.profileActivitiesService
                    .displayActivitiesTabs(categoryFacetsObject)
                    .pipe(this.tabFilters$(tab.code));
            };

            const activities$ = from(
                this.configSvc.activityDisplayTabConfiguration(),
            ).pipe(concatMap(relevantTabsByCategories$), toArray());
            return activities$;
        }
        return of(null);
    }

    loadProfileImage(userID) {
        this.settingsService
            .getResearcherProfileImage(userID)
            .subscribe((data) => {
                if (data) {
                    this.profileImage = data;
                    this.profileImage =
                        this.windowRefService.nativeWindow.URL.createObjectURL(
                            data,
                        );
                } else if (this.researcherImageUrl) {
                    this.profileImage = this.researcherImageUrl;
                }
            });
    }

    loadProfileImageForPublicDisplay(urlIdentifier) {
        this.settingsService
            .getResearcherProfileImageForPublicDisplay(urlIdentifier)
            .subscribe((data) => {
                if (data) {
                    this.profileImage = data;
                    this.profileImage =
                        this.windowRefService.nativeWindow.URL.createObjectURL(
                            data,
                        );
                } else if (this.researcherImageUrl) {
                    this.profileImage = this.researcherImageUrl;
                }
            });
    }

    loadData(userID) {
        this.profileService
            .getResearcherProfileData(userID)
            .subscribe((data) => {
                this.s1Finish = true;
                const profileData = data as ResearcherProfile;
                if (profileData) {
                    this.profileDataService.parseData(profileData);
                }
                this.isDataLoaded = true;
            });
        this.profileService
            .getResearcherProfileOverview(userID)
            .subscribe((data) => {
                this.s2Finish = true;
                const overviewData = data as ResearcherOverview;
                if (overviewData) {
                    this.profileDataService.parseOverview(overviewData);
                }
            });
        this.profileService
            .getResearcherDataForSEO(userID)
            .subscribe((data) => {
                this.s3Finish = true;
                const profileData = data as ResearcherProfile;
                if (profileData) {
                    this.profileDataService.parseDataForSEO(profileData);
                    if (this.profileDataService.expertise?.value?.length > 0) {
                        const parser = new DOMParser();

                        this._descText = this.profileDataService.expertise.value
                            .map((section) =>
                                parser
                                    .parseFromString(section, 'text/html')
                                    .body.textContent.trim(),
                            )
                            .join(' ');
                    } else {
                        this._descText = this.translate.instant(
                            'research.meta.description',
                            { value: this.researcherName },
                        );
                    }
                }
            });
        this.profileService
            .getResearcherProfileMetrics(userID)
            .pipe(tap({ finalize: () => (this.s4Finish = true) }))
            .subscribe({
                next: (data) => this.profileDataService.parseMetrics(data),
                error: () => this.profileDataService.parseMetrics({}),
            });

        this.waitForAllSubscriptionsToComplete(0);
    }

    waitForAllSubscriptionsToComplete(i) {
        if (i === 600) {
            return;
        }
        setTimeout(() => {
            if (
                this.s1Finish &&
                this.s2Finish &&
                this.s3Finish &&
                this.s4Finish
            ) {
                this.seoSupport();
                return;
            } else {
                this.waitForAllSubscriptionsToComplete(++i);
            }
        }, 50);
    }

    seoSupport() {
        this.updatePageMetaTags();
        this.prepareSchemaJson();
    }

    updatePageMetaTags() {
        this.updatePageDescription();
        this.updateAuthorMetaTag();
        this.linkForCanonicalURL = this.getLinkForCanonicalURL(this.userID);
        this.SEOService.createLinkForCanonicalURL(this.linkForCanonicalURL);
        this.updatePropertyMetaTag(
            SEO.TITLE_PROP,
            this.researcherName + ' - ' + this.mainInstName,
        );
        this.updateDescriptionMetaTag();
        this.updateImageMetaTags();
        this.updatePropertyMetaTag(SEO.URL_PROP, this.linkForCanonicalURL);
        this.updatePropertyMetaTag(SEO.LOCALE_PROP, this.translate.currentLang);
        this.updatePropertyMetaTag(SEO.TYPE_PROP, 'profile');
        this.updatePropertyMetaTag(
            SEO.PROFILE_FIRST_NAME_PROP,
            this.profileDataService.firstName.value,
        );
        this.updatePropertyMetaTag(
            SEO.PROFILE_LAST_NAME_PROP,
            this.profileDataService.lastName.value,
        );
        this.updatePropertyMetaTag(SEO.PROFILE_USERNAME_PROP, this.userID); //TODO change in the future
    }

    prepareSchemaJson() {
        const json = {
            '@context': 'http://schema.org',
            '@type': 'Person',
            name: this.researcherName,
        };
        if (this.profileDataService.firstName.value) {
            json['givenName'] = this.profileDataService.firstName.value;
        }
        json['familyName'] = this.profileDataService.lastName.value;

        const nameVariantsArray = this.getNameVariantsArray();
        if (
            nameVariantsArray.length > 0 &&
            !(nameVariantsArray.length === 1 && !nameVariantsArray[0])
        ) {
            //for some reason sometimes in the researchPersonData obj in the server, nameVariants contains empty obj
            json['additionalName'] = nameVariantsArray;
        }
        if (this.profileDataService.position.value) {
            this.translate
                .get(this.profileDataService.position.value)
                .subscribe((res) => {
                    json['jobTitle'] = res;
                });
        }
        json['url'] = this.linkForCanonicalURL;
        json['description'] = this._descText;

        /* Removed for URM-195472. Unclear from https://schema.org/Person how we can correctly utilize this field; but after the fix to description, these would just be the same anyway.
        if (
            this.profileDataService.expertise.value &&
            this.profileDataService.expertise.value.length > 0
        ) {
            json['disambiguationDescription'] =
                this.profileDataService.expertise.value.join(' ');
        }
        */
        if (this.profileDataService.userTitle.value) {
            this.translate
                .get(this.profileDataService.userTitle.value)
                .subscribe((res) => {
                    json['honorificPrefix'] = res;
                });
        }

        const currAffiliationArray = this.getCurrAffiliationArray();
        if (currAffiliationArray.length > 0) {
            json['affiliation'] = currAffiliationArray;
        }

        const prevAffiliationArray = this.getPrevAffiliationArray();
        if (prevAffiliationArray.length > 0) {
            json['alumniOf'] = prevAffiliationArray;
        }

        const associationsArray = this.getAssociationsArray();
        if (associationsArray.length > 0) {
            json['memberOf'] = associationsArray;
        }

        const identifierArray = this.getIdentifierArray();
        if (identifierArray.length > 0) {
            json['identifier'] = identifierArray;
        }
        this.SEOService.createSchemaDotOrgScript(json);
    }

    getIdentifierArray() {
        const identifierArray = [];
        this.profileDataService.identifiers.value.forEach(
            (identifier: Identifier) => {
                const identifierObj = {
                    '@type': 'PropertyValue',
                    propertyID: identifier.type,
                    value: identifier.value,
                };
                identifierArray.push(identifierObj);
            },
        );
        return identifierArray;
    }

    getAssociationsArray() {
        const associationsArray = [];
        this.profileDataService.associations.value.forEach((association) => {
            const memberOf = {
                '@type': 'Organization',
                name: association,
            };
            associationsArray.push(memberOf);
        });
        return associationsArray;
    }

    getPrevAffiliationArray() {
        const prevAffiliationArray = [];
        this.profileDataService.prevAffiliations.value.forEach(
            (aff: PersonOrganizationAffiliationDataWrapper) => {
                const affObj = {
                    '@type': 'EducationalOrganization',
                    name: aff.data.organizationName,
                };
                prevAffiliationArray.push(affObj);
            },
        );
        return prevAffiliationArray;
    }

    getCurrAffiliationArray() {
        const currAffiliationArray = [];
        this.profileDataService.curAffiliations.value.forEach(
            (aff: PersonOrganizationAffiliationDataWrapper) => {
                let name = aff.data.organizationName;
                if (aff.category === 'INTERNAL') {
                    name = name + ', ' + this.mainInstName;
                }
                const affObj = {
                    '@type': 'EducationalOrganization',
                    name,
                };
                currAffiliationArray.push(affObj);
            },
        );
        return currAffiliationArray;
    }

    getNameVariantsArray() {
        const nameVariantsArray = [];
        this.profileDataService.nameVariants.value.forEach(
            (name: ResearcherName) => {
                const fullName = [
                    name.firstName,
                    name.middleName,
                    name.lastName,
                    name.nameSuffix,
                ]
                    .filter(Boolean)
                    .join(' ');
                nameVariantsArray.push(fullName);
            },
        );
        return nameVariantsArray;
    }

    updateImageMetaTags() {
        if (this.researcherImageUrl) {
            this.updatePropertyMetaTag(SEO.IMAGE_PROP, this.researcherImageUrl);
            this.updatePropertyMetaTag(SEO.IMAGE_WIDTH_PROP, '200px');
            this.updatePropertyMetaTag(SEO.IMAGE_HEIGHT_PROP, '200px');
            this.translate
                .get('research.meta.image.alt', { value: this.researcherName })
                .subscribe((res) => {
                    this.updatePropertyMetaTag(SEO.IMAGE_ALT_PROP, res);
                });
        } else {
            this.SEOService.removeMetaTag("property='og:image'");
            this.SEOService.removeMetaTag("property='og:image:width'");
            this.SEOService.removeMetaTag("property='og:image:height'");
            this.SEOService.removeMetaTag("property='og:image:alt'");
        }
    }

    get researcherImageUrl() {
        return this.profileDataService?.researcherImageUrl?.value;
    }

    updatePageDescription() {
        this.SEOService.updateDescription(this._descText);
    }

    updateDescriptionMetaTag() {
        this.updatePropertyMetaTag(SEO.DESCRIPTION_PROP, this._descText);
    }

    updateAuthorMetaTag() {
        this.translate.get('research.meta.author').subscribe((res) => {
            this.SEOService.updateMetaTag(SEO.AUTHOR_META, res);
        });
    }

    updatePropertyMetaTag(property, content) {
        this.SEOService.updatePropertyMetaTag(property, content);
    }

    goToSettings() {
        this.router.navigate([State.SETTINGS], {
            queryParams: { institution: this.institutionCode },
        });
    }

    get researcherName() {
        return this.profileDataService.displayName.value;
    }

    getLinkForCanonicalURL(userID) {
        return (
            this.configSvc.externalFacingBaseUrl() +
            '/esploro/profile/' +
            userID
        );
    }

    showTitleOrPosition(affiliation: any, position: string) {
        return (
            (affiliation && (affiliation.title || affiliation.position)) ||
            position
        );
    }

    ngOnDestroy() {
        if (this.nameSubscription) {
            this.nameSubscription.unsubscribe();
        }
        this._onDestroy.next();
        this._onDestroy.complete();
    }

    goBack() {
        this.routerService.goBack();
    }
}
